import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService, type User, type LoginCredentials, type UpdateProfileData } from '@/services'
import { HttpClient } from '@/services/http'
import { useWhitelabel } from '@/composables/useWhitelabel'
import { setSentryUserContext, setSentryTenantContext } from '@/plugins/sentry-context'

export type { User } from '@/services'

export const useAuthStore = defineStore('auth', () => {
  // Estado reativo
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string>('')

  // Getters computados
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userName = computed(() => user.value?.name || '')
  const userRole = computed(() => user.value?.accessLevel?.name || 'guest')
  const userDisplayRole = computed(() => user.value?.accessLevel?.displayName || 'Usuário')
  const userLevel = computed(() => user.value?.accessLevel?.level || 0)
  const userPermissions = computed(() => user.value?.accessLevel?.permissions || [])
  const userEmail = computed(() => user.value?.email || '')
  const userGroup = computed(() => user.value?.group || null)
  const userSector = computed(() => user.value?.sector || null)
  const userAvatar = computed(() => user.value?.avatar || '/media/avatars/gray/1.jpg')
  const isSupervisor = computed(() => userLevel.value >= 50)
  const isAdmin = computed(() => userLevel.value >= 80)

  // Função para inicializar whitelabel após login
  const initializeWhitelabel = async () => {
    try {
      const whitelabel = useWhitelabel()
      await whitelabel.loadWhitelabelConfig()
      whitelabel.applyWhitelabelStyles()
      console.log('✅ Whitelabel inicializado com sucesso')
    } catch (error) {
      console.warn('⚠️ Erro ao inicializar whitelabel, usando valores padrão:', error)
      // Aplicar estilos padrão mesmo com erro
      const whitelabel = useWhitelabel()
      whitelabel.applyWhitelabelStyles()
    }
  }

  // Actions
  const login = async (credentials: LoginCredentials) => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await authService.login(credentials)
      
      // Atualizar estado local
      user.value = response.user
      token.value = response.accessToken
      
      // Inicializar whitelabel após login bem-sucedido
      await initializeWhitelabel()
      
      return { success: true, user: response.user }
    } catch (err: any) {
      // Usar o método estático do HttpClient para formatar mensagens de erro
      const errorMessage = HttpClient.getFirstErrorMessage
        ? HttpClient.getFirstErrorMessage(err)
        : (Array.isArray(err.message) ? err.message.join(', ') : err.message || 'Erro ao fazer login')
      
      error.value = errorMessage
      console.error('❌ Login error:', err)
      
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    isLoading.value = true
    try {
      await authService.logout()
    } catch (err) {
      console.warn('⚠️ Logout error:', err)
    } finally {
      // Limpar estado local
      user.value = null
      token.value = null
      error.value = ''
      isLoading.value = false
    }
  }

  const checkAuth = async (): Promise<boolean> => {
    try {
      // Se já temos usuário e token válidos, não precisamos verificar novamente
      if (user.value && token.value) {
        console.log('✅ Usuário já autenticado com dados válidos')
        return true
      }

      // Verificar se há token armazenado
      const currentToken = authService.getToken()
      
      if (currentToken) {
        token.value = currentToken
        
        // Tentar obter o perfil do usuário
        try {
          const profile = await authService.getProfile()
          user.value = profile
          setSentryUserContext({ id: profile.id, email: profile.email, username: profile.name })
          if (profile.tenantId) setSentryTenantContext({ id: profile.tenantId })

          // Inicializar whitelabel após obter perfil
          await initializeWhitelabel()

          return true
        } catch (err: any) {
          console.warn('⚠️ Failed to get profile, trying refresh token:', err)

          // Se falhar ao obter perfil, tentar refresh do token
          try {
            const newToken = await authService.refreshToken()
            token.value = newToken
            const profile = await authService.getProfile()
            user.value = profile
            setSentryUserContext({ id: profile.id, email: profile.email, username: profile.name })
            if (profile.tenantId) setSentryTenantContext({ id: profile.tenantId })

            // Inicializar whitelabel após refresh bem-sucedido
            await initializeWhitelabel()

            return true
          } catch (refreshErr: any) {
            console.warn('⚠️ Token refresh failed:', refreshErr)
            user.value = null
            token.value = null
            setSentryUserContext(null)
            setSentryTenantContext(null)
            return false
          }
        }
      }

      return false
    } catch (err) {
      console.warn('⚠️ Auth check error:', err)
      user.value = null
      token.value = null
      setSentryUserContext(null)
      setSentryTenantContext(null)
      return false
    }
  }

  const updateUser = async (userData: UpdateProfileData) => {
    isLoading.value = true
    error.value = ''

    try {
      const updatedUser = await authService.updateProfile(userData)
      user.value = updatedUser
      return { success: true, user: updatedUser }
    } catch (err: any) {
      const errorMessage = HttpClient.getFirstErrorMessage
        ? HttpClient.getFirstErrorMessage(err)
        : (err.message || 'Erro ao atualizar usuário')
      
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const updateUserAvatar = async (avatar: File) => {
    isLoading.value = true
    error.value = ''

    try {
      const updatedUser = await authService.updateProfile({ avatar })
      user.value = updatedUser
      return { success: true, user: updatedUser }
    } catch (err: any) {
      const errorMessage = HttpClient.getFirstErrorMessage
        ? HttpClient.getFirstErrorMessage(err)
        : (err.message || 'Erro ao atualizar avatar')
      
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const refreshProfile = async () => {
    try {
      const profile = await authService.getProfile()
      user.value = profile
      return profile
    } catch (err) {
      console.error('❌ Refresh profile error:', err)
      throw err
    }
  }

  const forgotPassword = async (email: string) => {
    isLoading.value = true
    error.value = ''

    try {
      await authService.forgotPassword({ email })
      return { success: true }
    } catch (err: any) {
      const errorMessage = HttpClient.getFirstErrorMessage
        ? HttpClient.getFirstErrorMessage(err)
        : (err.message || 'Erro ao solicitar recuperação de senha')
      
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const initializeAuth = async (): Promise<boolean> => {
    // Verificar se há token no localStorage ao inicializar
    const savedToken = authService.getToken()
    if (savedToken) {
      token.value = savedToken
      return await checkAuth()
    }
    return false
  }

  // Funções utilitárias para verificação de permissões
  const hasPermission = (permission: string): boolean => {
    return userPermissions.value.includes(permission)
  }

  const hasMinimumLevel = (level: number): boolean => {
    return userLevel.value >= level
  }

  return {
    // Estado
    user,
    token,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    userName,
    userRole,
    userDisplayRole,
    userLevel,
    userPermissions,
    userEmail,
    userGroup,
    userSector,
    userAvatar,
    isSupervisor,
    isAdmin,
    // Actions
    login,
    logout,
    checkAuth,
    updateUser,
    updateUserAvatar,
    refreshProfile,
    forgotPassword,
    initializeAuth,
    // Utility functions
    hasPermission,
    hasMinimumLevel
  }
})